/**
 * API-клиент для ucheta.ru
 * fetch-обёртка с автоматическим JWT refresh и перехватом 401/403
 *
 * Безопасность сессии:
 * - access_token — только в памяти (не localStorage / не document.cookie)
 * - refresh_token — HttpOnly; Secure; SameSite=Strict cookie с API
 * - auth_session — несекретный маркер для Next.js proxy (route guards)
 */

import {
  ApiError,
  parseTariffLimitPayload,
} from '@/lib/api-errors';

const API_BASE = process.env.NEXT_PUBLIC_API_URL ?? 'http://api.ucheta.local';
const COOKIE_DOMAIN = process.env.NEXT_PUBLIC_COOKIE_DOMAIN ?? '';

/** Несекретный cookie — маркер сессии для Next.js proxy (route guards) */
export const AUTH_SESSION_COOKIE = 'auth_session';

// Интерфейс ответа от API
interface ApiResponse<T = unknown> {
  data?: T;
  message?: string;
  errors?: Record<string, string[]>;
}

// Access-токен только в памяти процесса вкладки
let accessToken: string | null = null;
let refreshInFlight: Promise<boolean> | null = null;

export function getAccessToken(): string | null {
  return accessToken;
}

export function setAccessToken(access: string, remember: boolean = true): void {
  accessToken = access;
  if (typeof window !== 'undefined') {
    setAuthSessionHint(remember);
  }
}

/** @deprecated используйте setAccessToken — refresh больше не передаётся в JS */
export function setTokens(access: string, _refresh?: string, remember: boolean = true): void {
  setAccessToken(access, remember);
}

export function clearTokens(): void {
  accessToken = null;
  if (typeof window !== 'undefined') {
    clearAuthSessionHint();
    // Миграция: убрать старые небезопасные хранилища
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('remember_me');
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('refresh_token');
    document.cookie = 'access_token=; path=/; max-age=0; SameSite=Lax';
    document.cookie = 'refresh_token=; path=/; max-age=0; SameSite=Lax';
  }
}

/** Совместимость: раньше грузили токены из storage — теперь no-op для access. */
export function loadTokensFromStorage(): void {
  // access только в памяти; при F5 восстанавливается через /auth/refresh + cookie
}

function cookieDomainAttr(): string {
  return COOKIE_DOMAIN ? `domain=${COOKIE_DOMAIN}; ` : '';
}

function cookieSecureAttr(): string {
  return typeof window !== 'undefined' && window.location.protocol === 'https:'
    ? 'Secure; '
    : '';
}

function setAuthSessionHint(remember: boolean): void {
  const maxAge = remember ? 315360000 : 24 * 3600;
  document.cookie = `${AUTH_SESSION_COOKIE}=1; path=/; ${cookieDomainAttr()}max-age=${maxAge}; SameSite=Lax; ${cookieSecureAttr()}`;
}

function clearAuthSessionHint(): void {
  document.cookie = `${AUTH_SESSION_COOKIE}=; path=/; ${cookieDomainAttr()}expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax; ${cookieSecureAttr()}`;
  // На случай старой host-only cookie без Domain
  document.cookie = `${AUTH_SESSION_COOKIE}=; path=/; max-age=0; SameSite=Lax`;
}

export function hasAuthSessionHint(): boolean {
  if (typeof document === 'undefined') return false;
  return document.cookie.split(';').some((c) => c.trim().startsWith(`${AUTH_SESSION_COOKIE}=`));
}

async function refreshAccessToken(): Promise<boolean> {
  if (refreshInFlight) {
    return refreshInFlight;
  }

  refreshInFlight = (async () => {
    try {
      const response = await fetch(`${API_BASE}/api/v1/auth/refresh`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({}),
        cache: 'no-store',
      });

      if (response.ok) {
        const data = await response.json();
        if (typeof data.access_token === 'string') {
          // remember: сохраняем длинный hint, если маркер уже был
          const remember = hasAuthSessionHint();
          setAccessToken(data.access_token, remember);
          return true;
        }
      }
    } catch {
      // Refresh не удался
    }

    clearTokens();
    return false;
  })();

  try {
    return await refreshInFlight;
  } finally {
    refreshInFlight = null;
  }
}

/** Восстановить access-токен из HttpOnly refresh-cookie (после F5). */
export async function restoreSession(): Promise<boolean> {
  if (accessToken) {
    return true;
  }
  if (!hasAuthSessionHint()) {
    return false;
  }
  return refreshAccessToken();
}

// Основная функция запроса с автоматическим retry после refresh
export async function apiRequest<T = unknown>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  const isFormData = options.body instanceof FormData;
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(options.headers as Record<string, string> ?? {}),
  };

  if (!isFormData && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const url = endpoint.startsWith('http') ? endpoint : `${API_BASE}${endpoint}`;
  const isAuthLogin = endpoint.includes('/auth/login');
  const isAuthRefresh = endpoint.includes('/auth/refresh');

  let response = await fetch(url, {
    cache: 'no-store',
    ...options,
    headers,
    credentials: 'include',
  });

  // Автоматический refresh при 401 (кроме login/refresh)
  if (response.status === 401 && !isAuthLogin && !isAuthRefresh) {
    const refreshed = await refreshAccessToken();
    if (refreshed && accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
      response = await fetch(url, {
        cache: 'no-store',
        ...options,
        headers,
        credentials: 'include',
      });
    }
  }

  if (response.status === 403) {
    const forbiddenJson = await response.clone().json().catch(() => null);
    const tariffPayload = parseTariffLimitPayload(forbiddenJson);

    if (tariffPayload) {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('api:tariff-limit', { detail: tariffPayload }));
      }
      throw new ApiError(tariffPayload.message, tariffPayload.error, tariffPayload);
    }

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('api:forbidden', { detail: forbiddenJson }));
    }
    const forbiddenMsg = forbiddenJson?.message ?? forbiddenJson?.error;
    throw new Error(
      typeof forbiddenMsg === 'string' ? forbiddenMsg : 'Недостаточно прав для выполнения операции',
    );
  }

  if (response.status === 401 && !isAuthLogin && !isAuthRefresh) {
    clearTokens();
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('api:unauthorized'));
    }
    throw new Error('Сессия истекла, выполните вход заново');
  }

  const json = await response.json().catch(() => null);

  if (!response.ok && response.status !== 422) {
    const errMsg = json?.message ?? json?.error;
    throw new Error(typeof errMsg === 'string' ? errMsg : `HTTP ${response.status}`);
  }

  if (!json) {
    return {} as ApiResponse<T>;
  }

  if (typeof json === 'object' && !('data' in json)) {
    return {
      data: json as T,
      ...json
    } as ApiResponse<T>;
  }

  return json as ApiResponse<T>;
}

// Удобные обёртки для GET/POST/PUT/DELETE
export const api = {
  get:    <T>(url: string, opts?: RequestInit) =>
    apiRequest<T>(url, { ...opts, method: 'GET' }),

  post:   <T>(url: string, body: unknown, opts?: RequestInit) =>
    apiRequest<T>(url, { ...opts, method: 'POST', body: body instanceof FormData ? body : JSON.stringify(body) }),

  put:    <T>(url: string, body: unknown, opts?: RequestInit) =>
    apiRequest<T>(url, { ...opts, method: 'PUT', body: body instanceof FormData ? body : JSON.stringify(body) }),

  patch:  <T>(url: string, body: unknown, opts?: RequestInit) =>
    apiRequest<T>(url, { ...opts, method: 'PATCH', body: JSON.stringify(body) }),

  delete: <T>(url: string, opts?: RequestInit) =>
    apiRequest<T>(url, { ...opts, method: 'DELETE' }),

  postForm: <T>(url: string, body: FormData, opts?: RequestInit) =>
    apiRequest<T>(url, { ...opts, method: 'POST', body }),

  downloadBlob: async (endpoint: string, accept = 'application/pdf'): Promise<Blob> => {
    return api.downloadBlobRequest(endpoint, { method: 'GET' }, accept);
  },

  downloadBlobPost: async (
    endpoint: string,
    body: unknown,
    accept = 'application/pdf',
  ): Promise<Blob> => {
    return api.downloadBlobRequest(
      endpoint,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
      accept,
    );
  },

  downloadBlobRequest: async (
    endpoint: string,
    init: RequestInit,
    accept = 'application/pdf',
  ): Promise<Blob> => {
    const headers: Record<string, string> = {
      Accept: `${accept}, application/json;q=0.9`,
      ...(init.headers as Record<string, string> | undefined),
    };
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }
    const url = endpoint.startsWith('http') ? endpoint : `${API_BASE}${endpoint}`;
    let response = await fetch(url, { ...init, cache: 'no-store', headers, credentials: 'include' });

    if (response.status === 401) {
      const refreshed = await refreshAccessToken();
      if (refreshed && accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
        response = await fetch(url, { ...init, cache: 'no-store', headers, credentials: 'include' });
      }
    }

    if (!response.ok) {
      const json = await response.json().catch(() => null);
      const tariffPayload = parseTariffLimitPayload(json);
      if (tariffPayload) {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('api:tariff-limit', { detail: tariffPayload }));
        }
        throw new ApiError(tariffPayload.message, tariffPayload.error, tariffPayload);
      }
      const errMsg = json?.message ?? json?.error;
      throw new Error(typeof errMsg === 'string' ? errMsg : `HTTP ${response.status}`);
    }

    return response.blob();
  },
};

export default api;
